import React, { useEffect, useMemo, useRef, useState } from "react"
import { useAppSelector } from "@/store/hooks"
import { allPieces } from "@/utils/data"
import { getCellByOrderAndMapId, getCellGroup } from "@/utils/gameHelpers"
import { actionCardTitles } from "@/utils/actionCardTitles"
import { mapDesigns } from "@mono/mapDesigns"
import { cardMoneyFlow } from "@mono/constants"
import classNames from "classnames"

// The set of history events surfaced in the live game log.
const LOGGED_TYPES: ReadonlyArray<IHistoryItem["type"]> = [
  "auction-won",
  "auction-tied",
  "stock-invested",
  "stock-retrieved",
  "card-played",
  "house-bought",
  "house-sold",
  "rent-paid",
  "private-buy",
  "steal-tax",
  "player-replaced-by-bot",
]

// Piece colors are used as-is unless too light to read on the panel's light
// background, in which case they're darkened to a readable luminance.
const readablePieceColor = (color?: string): string | undefined => {
  if (!color) return undefined
  const hex = color.match(/^#([0-9a-f]{6})$/i)?.[1]
  if (!hex) return color // named CSS colors in the piece data are already dark
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16))
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b
  if (luminance <= 130) return color
  const scale = 130 / luminance
  const toHex = (v: number) =>
    Math.round(v * scale)
      .toString(16)
      .padStart(2, "0")
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

// A small chip showing a player's piece and username, tinted with the piece color.
const PlayerChip: React.FC<{
  pieceImg?: string
  color?: string
  name: string
}> = ({ pieceImg, color, name }) => {
  const tinted = readablePieceColor(color)
  return (
    <span className="inline-flex items-center gap-1 align-middle">
      {pieceImg && (
        <img
          src={pieceImg}
          alt=""
          className="h-3.5 w-3.5 shrink-0 object-contain"
        />
      )}
      <span
        className={classNames(
          "inline-block max-w-[8rem] truncate align-bottom font-semibold",
          !tinted && "text-black/80"
        )}
        style={tinted ? { color: tinted } : undefined}
        title={name}
      >
        {name}
      </span>
    </span>
  )
}

const money = (amount: number) => `M${(amount || 0).toLocaleString()}`

// Fanned pair of playing cards used for action-card events (Font Awesome free has
// no cards glyph). The diamond pip is punched out of the front card with an evenodd
// fill so the badge color shows through it at any size.
const ActionCardIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    aria-hidden="true"
    fill="currentColor"
  >
    <rect
      x="8.5"
      y="3.5"
      width="10.5"
      height="14.5"
      rx="2"
      transform="rotate(12 13.75 10.75)"
      opacity="0.45"
    />
    <g transform="rotate(-8 9.75 13.25)">
      <path
        fillRule="evenodd"
        d="M6.5 6h6.5a2 2 0 0 1 2 2v10.5a2 2 0 0 1-2 2H6.5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Zm3.25 4.4 2.35 2.85-2.35 2.85-2.35-2.85Z"
      />
    </g>
  </svg>
)

// Consecutive house buy/sell items (one per property from a single property-management
// action) are merged into a single entry for display, summing the house count and
// dropping the property names.
interface CombinedHouseEntry {
  type: "house-combined"
  action: "bought" | "sold"
  playerOrder: PlayerOrder
  count: number
}
type LogEntry = IHistoryItem | CombinedHouseEntry

// How close (px) to the bottom the user must be for auto-scroll to keep following.
const AUTOSCROLL_THRESHOLD = 40

const GameLogPanel: React.FC = () => {
  const historyItems = useAppSelector(
    (state) => state.global.myGame?.situation?.historyItems
  )
  const situationPlayers = useAppSelector(
    (state) => state.global.myGame?.situation?.players
  )
  const gamePlayers = useAppSelector((state) => state.global.myGame?.players)
  const mapId = useAppSelector((state) => state.global.myGame?.settings?.mapId)

  const scrollRef = useRef<HTMLDivElement>(null)
  // Tracks whether the user is scrolled near the bottom; auto-scroll only follows
  // new entries while this is true, so scrolling up to read history isn't hijacked.
  const isNearBottomRef = useRef(true)
  const [hasNewEvents, setHasNewEvents] = useState(false)

  // Resolve a player's order to their username + piece for display.
  const describePlayer = useMemo(() => {
    return (order?: PlayerOrder) => {
      const situationPlayer = situationPlayers?.find((p) => p.order === order)
      const user = gamePlayers?.find((u) => u.email === situationPlayer?.id)
      const piece = allPieces.find((p) => p.id === situationPlayer?.pieceId)
      return {
        name: user?.username || `Player ${order ?? "?"}`,
        pieceImg: piece?.imgSrc,
        color: piece?.color,
      }
    }
  }, [situationPlayers, gamePlayers])

  const propertyName = useMemo(() => {
    return (order?: number) => {
      if (order === undefined || !mapId) return "a property"
      return (
        getCellByOrderAndMapId(order, mapId)?.propertyDetails?.name ||
        "a property"
      )
    }
  }, [mapId])

  // Returns the group color for a property, or null if it's a station (no rentData).
  const propertyColor = useMemo(() => {
    return (order?: number): string | null => {
      if (order === undefined || !mapId) return null
      const cell = getCellByOrderAndMapId(order, mapId)
      if (!cell?.propertyDetails) return null
      if (!cell.propertyDetails.rentData) return null // station
      const group = getCellGroup(mapId, cell)
      return group?.color ?? null
    }
  }, [mapId])

  // Renders a property name — a small group-color swatch next to dark text (group
  // colors are often too light to be readable as text color), or amber text for stations.
  const PropertyName = ({ order }: { order?: number }) => {
    const name = propertyName(order)
    const color = propertyColor(order)
    if (color) {
      return (
        <span className="inline-flex items-center gap-1 align-middle">
          <span
            className="h-2 w-2 shrink-0 rounded-sm ring-1 ring-black/10"
            style={{ backgroundColor: color }}
          />
          <span className="font-semibold text-black/80">{name}</span>
        </span>
      )
    }
    return <span className={`font-semibold ${c.amber}`}>{name}</span>
  }

  const cardName = useMemo(() => {
    const map = mapDesigns.find((m) => m.id === mapId)
    return (cardId: ActionCardId) =>
      actionCardTitles[cardId] ||
      map?.allChances.find((c) => c.id === cardId)?.text ||
      "a card"
  }, [mapId])

  // Build the display list chronologically (merging adjacent house buy/sell runs).
  // Merging happens on the RAW history (before filtering) so that other event
  // types act as natural separators — preventing house events from two different
  // property-management transactions being folded into one.
  // Each entry carries the raw-history index of its first item as a stable React
  // key (history is append-only, so raw indices never shift).
  const visibleItems = useMemo<{ entry: LogEntry; key: number }[]>(() => {
    const merged: { entry: LogEntry; key: number }[] = []
    ;(historyItems || []).forEach((item, rawIndex) => {
      if (item.type === "house-bought" || item.type === "house-sold") {
        const action = item.type === "house-bought" ? "bought" : "sold"
        const last = merged[merged.length - 1]
        // Fold into the previous entry only when it's literally the next item in
        // the raw history (same player, same action, no other event in between).
        if (
          last &&
          last.entry.type === "house-combined" &&
          last.entry.action === action &&
          last.entry.playerOrder === item.playerOrder
        ) {
          last.entry.count += item.count
          return
        }
        merged.push({
          entry: {
            type: "house-combined",
            action,
            playerOrder: item.playerOrder,
            count: item.count,
          },
          key: rawIndex,
        })
      } else {
        merged.push({ entry: item, key: rawIndex })
      }
    })
    // Filter to only displayable types after merging.
    return merged.filter(
      ({ entry }) =>
        entry.type === "house-combined" ||
        LOGGED_TYPES.includes((entry as IHistoryItem).type)
    )
  }, [historyItems])

  const scrollToBottom = (smooth?: boolean) => {
    const el = scrollRef.current
    if (el)
      el.scrollTo({
        top: el.scrollHeight,
        behavior: smooth ? "smooth" : "auto",
      })
  }

  // Follow new entries only while the user is already at the bottom; otherwise
  // surface a "new events" pill instead of yanking them out of their scrollback.
  useEffect(() => {
    if (isNearBottomRef.current) scrollToBottom()
    else setHasNewEvents(true)
  }, [visibleItems.length])

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    const nearBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight < AUTOSCROLL_THRESHOLD
    isNearBottomRef.current = nearBottom
    if (nearBottom) setHasNewEvents(false)
  }

  // A saturated gradient tile per event type with a white glyph — reads as crisp
  // app iconography and makes each event type scannable by color at a glance.
  // `bg` holds gradient stops; the shared badge element supplies bg-gradient-to-br.
  const itemIcon = (
    item: LogEntry
  ): { icon: string; bg: string; svg?: boolean } => {
    switch (item.type) {
      case "auction-won":
        return { icon: "fa-gavel", bg: "from-amber-400 to-amber-600" }
      case "auction-tied":
        // Balanced scale = tie, so the gavel stays unique to auction wins.
        return { icon: "fa-scale-balanced", bg: "from-slate-400 to-slate-500" }
      case "stock-invested":
        return {
          icon: "fa-arrow-trend-up",
          bg: "from-emerald-400 to-emerald-600",
        }
      case "stock-retrieved":
        return { icon: "fa-arrow-trend-down", bg: "from-teal-400 to-teal-600" }
      case "card-played":
        return { icon: "", bg: "from-violet-400 to-violet-600", svg: true }
      case "house-combined":
        // Sold gets its own glyph (house + x) and hue, not just a grayed house.
        return item.action === "bought"
          ? { icon: "fa-house", bg: "from-sky-400 to-sky-600" }
          : {
              icon: "fa-house-circle-xmark",
              bg: "from-orange-400 to-orange-600",
            }
      case "rent-paid":
        return {
          icon: "fa-hand-holding-dollar",
          bg: "from-rose-400 to-rose-600",
        }
      case "private-buy":
        // Key = taking ownership of a property (cart read as generic shopping).
        return { icon: "fa-key", bg: "from-indigo-400 to-indigo-600" }
      case "steal-tax":
        // Bandit mask on a dark tile = money stolen, distinct from honest earnings.
        return { icon: "fa-mask", bg: "from-zinc-600 to-zinc-800" }
      case "player-replaced-by-bot":
        // Same sky hue as the BOT badge on the player card, so the log entry and
        // the seat it refers to read as the same thing.
        return { icon: "fa-robot", bg: "from-sky-400 to-sky-600" }
      default:
        return { icon: "fa-circle-info", bg: "from-slate-400 to-slate-500" }
    }
  }

  // Semantic money colors: green = money received, red = money lost, amber =
  // neutral purchase price (amber also colors station names). Purple = card titles.
  const c = {
    amber: "text-amber-700",
    green: "text-green-700",
    red: "text-red-700",
    purple: "text-purple-700",
  }

  // Recency styling. Hierarchy is conveyed purely through font size — the newest
  // three entries step down in size (newest biggest), and everything older shares
  // one compact size. No opacity fade: all entries stay fully opaque.
  const tier = (distFromEnd: number) => {
    const recent = distFromEnd <= 2
    return {
      py: "py-[0.1875rem]",
      text:
        distFromEnd === 0
          ? "text-[1.0625rem]"
          : distFromEnd === 1
          ? "text-[1rem]"
          : distFromEnd === 2
          ? "text-[0.9375rem]"
          : "text-[0.875rem]",
      weight: distFromEnd === 0 ? "font-medium" : "font-normal",
      badge:
        distFromEnd === 0
          ? "w-7 h-7 rounded-lg"
          : recent
          ? "w-6 h-6 rounded-lg"
          : "w-5 h-5 rounded-md",
      icon:
        distFromEnd === 0
          ? "text-[0.875rem]"
          : recent
          ? "text-[0.75rem]"
          : "text-[0.6875rem]",
      svg:
        distFromEnd === 0
          ? "w-[1.125rem] h-[1.125rem]"
          : recent
          ? "w-4 h-4"
          : "w-3.5 h-3.5",
    }
  }

  const chip = (p: { name: string; pieceImg?: string; color?: string }) => (
    <PlayerChip {...p} />
  )

  const renderItem = (item: LogEntry): React.ReactNode => {
    switch (item.type) {
      case "auction-won": {
        const p = describePlayer(item.playerOrder)
        return (
          <>
            {chip(p)} won the auction for{" "}
            <PropertyName order={item.propertyOrder} /> at{" "}
            <span className={`font-semibold ${c.amber}`}>
              {money(item.amount)}
            </span>
          </>
        )
      }
      case "auction-tied": {
        return (
          <>
            Auction for <PropertyName order={item.propertyOrder} /> tied — no
            winner
          </>
        )
      }
      case "stock-invested": {
        const p = describePlayer(item.playerOrder)
        return (
          <>
            {chip(p)} invested{" "}
            <span className={`font-semibold ${c.amber}`}>
              {money(item.amount)}
            </span>{" "}
            in stocks
          </>
        )
      }
      case "stock-retrieved": {
        const p = describePlayer(item.playerOrder)
        return (
          <>
            {chip(p)} withdrew{" "}
            <span className={`font-semibold ${c.green}`}>
              {money(item.amount)}
            </span>{" "}
            from stocks
          </>
        )
      }
      case "card-played": {
        const p = describePlayer(item.playerOrder)
        // Cards that target nobody still arrive with a null targetedPlayerOrder
        // (the modal sends the unset selection through), so `!= null` is what
        // keeps "on Player ?" out of the log — `!== undefined` isn't enough.
        const target =
          item.targetedPlayerOrder != null &&
          item.targetedPlayerOrder !== item.playerOrder
            ? describePlayer(item.targetedPlayerOrder)
            : undefined
        return (
          <>
            {chip(p)} played{" "}
            <span className={`font-semibold ${c.purple}`}>
              {cardName(item.cardId)}
            </span>
            {target ? (
              <> on {chip(target)}</>
            ) : item.targetedPropertyOrder != null ? (
              <>
                {" "}
                on <PropertyName order={item.targetedPropertyOrder} />
              </>
            ) : null}
            {/* Money cards carry what they moved; the card name alone doesn't
                say whether it stung. Green when the player gathered it in, red
                when it was taken off someone. */}
            {item.amount ? (
              <>
                {" — "}
                <span
                  className={`font-semibold ${
                    cardMoneyFlow[item.cardId] === "collected" ? c.green : c.red
                  }`}
                >
                  {money(item.amount)}
                </span>{" "}
                {cardMoneyFlow[item.cardId] === "collected"
                  ? "collected"
                  : "charged"}
              </>
            ) : null}
          </>
        )
      }
      case "house-combined": {
        const p = describePlayer(item.playerOrder)
        return (
          <>
            {chip(p)} {item.action === "bought" ? "built" : "sold"} {item.count}{" "}
            {item.count === 1 ? "house" : "houses"}
          </>
        )
      }
      case "private-buy": {
        const p = describePlayer(item.playerOrder)
        return (
          <>
            {chip(p)} bought <PropertyName order={item.propertyOrder} />{" "}
            directly for{" "}
            <span className={`font-semibold ${c.amber}`}>
              {money(item.amount)}
            </span>
          </>
        )
      }
      case "steal-tax": {
        const p = describePlayer(item.playerOrder)
        return (
          <>
            {chip(p)} collected the{" "}
            <span className={`font-semibold ${c.green}`}>
              {money(item.amount)}
            </span>{" "}
            Steal Tax
          </>
        )
      }
      case "player-replaced-by-bot": {
        const p = describePlayer(item.playerOrder)
        return (
          <>
            {chip(p)} left the game — a{" "}
            <span className="font-semibold text-sky-700">bot</span> took over
            their seat
          </>
        )
      }
      case "rent-paid": {
        const payer = describePlayer(item.playerOrder)
        const owner =
          item.ownerOrder !== undefined
            ? describePlayer(item.ownerOrder)
            : undefined
        return (
          <>
            {chip(payer)} paid{" "}
            <span className={`font-semibold ${c.red}`}>{money(item.rent)}</span>{" "}
            rent
            {owner ? <> to {chip(owner)}</> : null} on{" "}
            <PropertyName order={item.propertyOrder} />
          </>
        )
      }
      default:
        return null
    }
  }

  return (
    <div className="relative h-full w-full">
      {/* Entrance animation for the newest log entry as it appears. */}
      <style>{`
        @keyframes logItemEnter {
          from { opacity: 0; transform: translateY(0.5rem) scale(0.96); }
          60%  { opacity: 1; }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        .log-item-enter { animation: logItemEnter 0.35s cubic-bezier(0.22, 1, 0.36, 1); }
      `}</style>
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        role="log"
        aria-live="polite"
        className="overflow-y-auto h-full w-full scrollbar-board"
      >
        {visibleItems.length === 0 ? null : (
          // Bottom-aligned (chat-style): with few entries the list sits at the
          // bottom of the panel; once it overflows it scrolls normally.
          <div className="flex min-h-full flex-col justify-end">
            <div className="relative flex flex-col pl-5">
              {/* Vertical timeline line — tied to the items so it never stretches
                  into the empty space above a short list. */}
              <div className="absolute left-[0.5625rem] top-2 bottom-2 w-px bg-black/[0.07]" />

              {visibleItems.map(({ entry, key }, i) => {
                const distFromEnd = visibleItems.length - 1 - i
                const t = tier(distFromEnd)
                const meta = itemIcon(entry)

                return (
                  <div
                    key={key}
                    className={classNames(
                      "relative flex items-center gap-2 rounded-md",
                      t.py,
                      distFromEnd === 0 && "log-item-enter"
                    )}
                  >
                    {/* Action icon badge */}
                    <div
                      className={classNames(
                        "shrink-0 flex items-center justify-center bg-gradient-to-br text-white overflow-hidden",
                        "ring-1 ring-black/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.35),0_1px_2px_rgba(0,0,0,0.2)]",
                        "transition-all duration-300",
                        t.badge,
                        meta.bg
                      )}
                    >
                      {meta.svg ? (
                        <ActionCardIcon
                          className={classNames(
                            t.svg,
                            "transition-all duration-300"
                          )}
                        />
                      ) : (
                        <i
                          className={classNames(
                            "fa-solid transition-[font-size] duration-300",
                            t.icon,
                            meta.icon
                          )}
                        />
                      )}
                    </div>
                    {/* Text */}
                    <span
                      className={classNames(
                        "font-poppins leading-snug text-black/75 min-w-0 transition-all duration-300",
                        t.text,
                        t.weight
                      )}
                    >
                      {renderItem(entry)}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
      {/* Shown when new entries arrive while the user is scrolled up in history. */}
      {hasNewEvents && (
        <button
          type="button"
          onClick={() => {
            scrollToBottom(true)
            setHasNewEvents(false)
          }}
          className="absolute bottom-2 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-black/70 px-2.5 py-1 font-poppins text-[0.6875rem] font-medium text-white shadow-md transition-colors hover:bg-black/80"
        >
          <i
            className="fa-solid fa-arrow-down text-[0.625rem]"
            aria-hidden="true"
          />
          New events
        </button>
      )}
    </div>
  )
}

export default React.memo(GameLogPanel)
